// HeroV2 — BestRx infomercial hero (customer-approved direct-response layout).
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AnnouncementTicker from "./AnnouncementTicker";
import LangSwitcher from "@/components/shared/LangSwitcher";
import {
  Shield,
  ShieldCheck,
  Stethoscope,
  Lock,
  Truck,
  Phone,
  ArrowRight,
  Check,
  Star,
  Tag,
  Zap,
  ShieldOff,
  MapPinned,
  FlaskConical,
  Building2,
  PhoneCall,
  Menu,
  X,
  Sun,
} from "lucide-react";
import { useLeadModal } from "@/components/shared/LeadProvider";

const RED = "#E11D2A";
const BLACK = "#0A0A0A";
const PHONE = "844-703-1780";
const TEL = "tel:+18447031780";

const NAV_LINKS = [
  { label: "HOME",         to: "/" },
  { label: "WEIGHT LOSS",  to: "/category/weight-loss" },
  { label: "MEN'S HEALTH", to: "/mens-garage" },
  { label: "WOMEN'S",      to: "/womens-shop" },
  { label: "WELLNESS",     to: "/category/wellness" },
  { label: "MEDICATIONS",  to: "/#medication-zone" },
  { label: "RESULTS",      to: "/results" },
  { label: "RESOURCES",    to: "/learn" },
  { label: "FAQ",          to: "/faq" },
  { label: "PROVIDERS",    to: "/providers" },
  { label: "ABOUT",        to: "/about" },
  { label: "CONTACT",      to: "/contact" },
];

const HEADER_TRUST = [
  { Icon: ShieldOff,   t: "NO INSURANCE",   s: "REQUIRED" },
  { Icon: Stethoscope, t: "PROVIDER",       s: "REVIEWED" },
  { Icon: Lock,        t: "DISCREET",       s: "& PRIVATE" },
  { Icon: Truck,       t: "DELIVERED",      s: "TO YOUR DOOR" },
];

const HERO_BULLETS = [
  "NO INSURANCE REQUIRED",
  "PROVIDER PRESCRIBED",
  "PHARMACY COMPOUNDED",
  "DELIVERED TO YOUR DOOR",
  "STARTING AS LOW AS $39 WEEK 1*",
];

const TRUST_CIRCLES = [
  { Icon: ShieldOff,    t: "NO INSURANCE",      s: "REQUIRED" },
  { Icon: Stethoscope,  t: "PROVIDER",          s: "REVIEWED" },
  { Icon: Tag,          t: "LOWEST PRICE",      s: "GUARANTEE*" },
  { Icon: Zap,          t: "FAST & SIMPLE",     s: "PROCESS" },
  { Icon: Truck,        t: "DISCREET",          s: "SHIPPING" },
  { Icon: MapPinned,    t: "TEXAS",             s: "BASED" },
];

/* ─────────  HEADER (top bar with logo + 4 trust + call us)  ───────── */
function TopHeader({ onCta }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const NAVY = "#07111F";

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header className="border-b border-white/5" style={{ background: NAVY }} data-testid="hero-header">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-3 md:py-4 flex items-center justify-between gap-3 md:gap-6">
        {/* Logo — image asset (BESTRX badge on desktop, BRX shield on tiny mobile) */}
        <Link to="/" className="shrink-0 inline-flex items-center" data-testid="brand-logo" aria-label="BestRx home">
          <img
            src="/assets/bestrx-logo-main.png"
            alt="BestRx — The Best Deal in Weight Loss"
            className="hidden sm:block h-12 md:h-14 lg:h-[58px] w-auto select-none"
            draggable={false}
          />
          <img
            src="/assets/bestrx-shield.png"
            alt="BestRx"
            className="sm:hidden h-11 w-auto select-none"
            draggable={false}
          />
        </Link>

        {/* 4 trust icons — desktop only */}
        <div className="hidden md:flex items-center gap-4 lg:gap-7">
          {HEADER_TRUST.map(({ Icon, t, s }) => (
            <div key={t} className="flex flex-col items-center text-center min-w-[80px]" data-testid={`hdr-trust-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <Icon size={22} strokeWidth={2.2} className="text-white mb-1" />
              <p className="font-extrabold text-[9.5px] leading-none text-white tracking-[0.05em]">{t}</p>
              <p className="text-[8.5px] mt-0.5 text-white/60 tracking-[0.04em]">{s}</p>
            </div>
          ))}
        </div>

        {/* Right side: Call us block (desktop) + CTA (always) + Hamburger (mobile only) */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="hidden lg:block text-right">
            <p className="font-extrabold text-[9px] sm:text-[10px] tracking-[0.18em] text-white">CALL US NOW</p>
            <a href={TEL} className="font-heavy block leading-none" style={{ color: RED, fontSize: "clamp(20px, 2.2vw, 30px)" }} data-testid="header-phone">
              {PHONE}
            </a>
            <p className="text-[8.5px] mt-0.5 font-bold text-white tracking-[0.04em]">
              WE WILL CALL YOU IN <span style={{ color: RED }}>30 MINUTES*</span>
            </p>
            <p className="text-[8.5px] text-white/55 tracking-wide">DURING BUSINESS HOURS</p>
          </div>

          {/* Mobile phone icon link */}
          <a
            href={TEL}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border-2"
            style={{ borderColor: RED, color: RED }}
            data-testid="header-phone-mobile"
            aria-label={`Call ${PHONE}`}
          >
            <Phone size={18} strokeWidth={2.6} />
          </a>

          <button
            onClick={onCta}
            data-testid="header-cta"
            className="inline-flex items-center gap-1.5 px-3 sm:px-7 py-2.5 sm:py-4 font-extrabold uppercase text-white text-[11px] sm:text-[14px] tracking-[0.08em] whitespace-nowrap"
            style={{ background: RED, boxShadow: "0 8px 18px rgba(225,29,42,0.3)" }}
          >
            Start Now <ArrowRight size={16} strokeWidth={2.6} />
          </button>

          {/* Hamburger — mobile only */}
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/20 text-white"
          >
            <Menu size={22} strokeWidth={2.4} />
          </button>
        </div>
      </div>

      {/* RED NAV BAR — desktop / tablet only */}
      <nav className="w-full hidden md:block" style={{ background: RED }} data-testid="hero-redbar">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-2.5 flex items-center justify-center gap-2 sm:gap-4 lg:gap-7 overflow-x-auto scrollbar-none relative">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="font-extrabold text-white text-[10.5px] sm:text-[12px] tracking-[0.12em] hover:opacity-80 whitespace-nowrap"
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
          {/* Language switcher pinned to the far right of the red nav */}
          <div className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2">
            <LangSwitcher />
          </div>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[200] md:hidden bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setMobileOpen(false); }}
          data-testid="mobile-menu-overlay"
        >
          <aside className="absolute top-0 right-0 h-full w-[85%] max-w-[360px] flex flex-col" style={{ background: NAVY }}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10" style={{ background: RED }}>
              <p className="font-heavy text-white text-[16px] tracking-[0.08em]">MENU</p>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 text-white hover:bg-white/25"
              >
                <X size={18} strokeWidth={2.6} />
              </button>
            </div>

            {/* Links */}
            <nav className="flex-1 overflow-y-auto py-2">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="block px-6 py-3.5 font-extrabold text-white text-[13px] tracking-[0.12em] border-b border-white/5 hover:bg-white/5"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* Call CTA */}
            <div className="border-t border-white/10 p-5 space-y-3">
              <a
                href={TEL}
                className="flex items-center justify-center gap-2 w-full py-3.5 font-heavy text-white text-[14px] tracking-[0.08em]"
                style={{ background: RED }}
                data-testid="mobile-menu-phone"
              >
                <Phone size={16} strokeWidth={2.6} /> {PHONE}
              </a>
              <button
                onClick={() => { setMobileOpen(false); onCta(); }}
                className="w-full py-3.5 font-heavy text-white text-[14px] tracking-[0.08em]"
                style={{ background: "#0A2342" }}
                data-testid="mobile-menu-start"
              >
                START YOUR ASSESSMENT
              </button>
              <p className="text-center text-[10px] font-bold text-white/55 tracking-[0.1em]">
                CALL BACK IN <span style={{ color: RED }}>30 MIN*</span> · DURING BUSINESS HOURS
              </p>
              <div className="pt-2 flex justify-center">
                <LangSwitcher testid="mobile-lang-switcher" />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* ANNOUNCEMENT TICKER — admin-managed scrolling bar */}
      <AnnouncementTicker />
    </header>
  );
}

/* ─────────  HERO CORE  ───────── */
function HeroCore({ onCta }) {
  return (
    <section className="relative bg-[#FAFAF8] overflow-hidden" data-testid="hero-core">
      <div className="absolute inset-0 hero-grid-overlay pointer-events-none opacity-50" />
      <div className="rx-accent-strikes hidden lg:block" style={{ top: "30px", right: "10%" }} />

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        {/* LEFT — headline + bullets + CTA */}
        <div className="lg:col-span-6">
          <div className="rx-accent-bar rx-fade-up" />
          <h1
            className="rx-fade-up rx-stagger-1 font-heavy leading-[0.88]"
            style={{ fontSize: "clamp(40px, 5.4vw, 88px)" }}
            data-testid="hero-headline"
          >
            <span className="text-black">DON'T BELIEVE US.</span>
          </h1>
          <h1
            className="rx-fade-up rx-stagger-2 font-ultra mt-1 leading-[0.85]"
            style={{ fontSize: "clamp(80px, 12vw, 196px)", color: RED, textShadow: "0 8px 0 rgba(225,29,42,0.18)" }}
          >
            TRY US.
          </h1>

          <h2
            className="rx-fade-up rx-stagger-3 font-heavy mt-3 mb-4 leading-tight"
            style={{ fontSize: "clamp(20px, 2.4vw, 34px)" }}
          >
            THE <span style={{ color: RED }}>BEST DEAL</span> IN WEIGHT LOSS.
          </h2>

          <div
            className="rx-fade-up rx-stagger-4 inline-block bg-black text-white font-heavy px-5 py-2.5 mb-6"
            style={{ fontSize: "clamp(13px, 1.4vw, 18px)", letterSpacing: "0.03em" }}
            data-testid="lowest-prices-band"
          >
            LOWEST PRICES. REAL MEDICATION. REAL RESULTS.
          </div>

          {/* Red-check bullets */}
          <ul className="rx-fade-up rx-stagger-5 space-y-2.5 mb-7" data-testid="hero-bullets">
            {HERO_BULLETS.map((b) => (
              <li key={b} className="flex items-center gap-3">
                <span
                  className="inline-flex w-6 h-6 rounded-full items-center justify-center shrink-0"
                  style={{ background: RED, boxShadow: "0 4px 8px rgba(225,29,42,0.35)" }}
                >
                  <Check size={14} color="white" strokeWidth={4} />
                </span>
                <span className="font-extrabold text-[13.5px] md:text-[15px] tracking-[0.06em] text-black uppercase">
                  {b}
                </span>
              </li>
            ))}
          </ul>

          {/* CAN YOU WAIT A WEEK red stamp */}
          <div
            className="rx-fade-up rx-stagger-6 inline-block overflow-hidden cursor-pointer select-none"
            onClick={onCta}
            data-testid="wait-a-week-stamp"
            style={{ boxShadow: "0 16px 36px rgba(225,29,42,0.30)" }}
          >
            <div className="px-7 py-4 text-white font-heavy leading-none" style={{ background: RED, fontSize: "clamp(20px, 2.2vw, 30px)" }}>
              CAN YOU WAIT A WEEK?
              <div className="text-[12px] mt-1.5 font-extrabold tracking-[0.12em]">THEN WHY PAY MORE?</div>
            </div>
            <div className="px-7 py-3 text-white font-extrabold tracking-[0.12em] text-[12.5px] md:text-[14px] bg-black">
              START FOR JUST <span style={{ color: RED }}>$39 WEEK 1*</span>
            </div>
          </div>

          {/* SUMMER SPECIAL coupon — mobile only (appears between hero copy and meds image) */}
          <div className="lg:hidden mt-7" data-testid="summer-coupon-mobile">
            <SummerSpecialCoupon onCta={onCta} compact />
          </div>
        </div>

        {/* RIGHT — Medication packshot + coupon overlay */}
        <div className="lg:col-span-6 relative">
          {/* SUMMER SPECIAL coupon — desktop, floating top-right */}
          <div className="hidden lg:block absolute -top-4 right-0 z-20 max-w-[260px] rx-fade-up rx-stagger-3" data-testid="summer-coupon-desktop">
            <SummerSpecialCoupon onCta={onCta} />
          </div>

          <img
            src="/assets/hero-meds.png?v=3"
            alt="Tirzepatide multi-dose lineup + BESTRX injection pen + capsules"
            className="relative w-full h-auto select-none max-h-[680px] object-contain z-10"
            draggable={false}
            data-testid="hero-meds-image"
          />
          <div className="rx-pedestal" />
          {/* PROVIDER PRESCRIBED callout — pushed below the coupon so they don't overlap */}
          <div className="hidden lg:flex absolute top-[170px] right-0 max-w-[260px] items-start gap-3" data-testid="hero-callout">
            <Shield size={28} strokeWidth={2.2} className="shrink-0 mt-1" />
            <div>
              <p className="font-heavy leading-[1.1] text-black" style={{ fontSize: "16px", letterSpacing: "-0.01em" }}>
                PROVIDER PRESCRIBED.
                <br />
                PHARMACY COMPOUNDED.
              </p>
              <p className="font-heavy mt-1 leading-[1.1]" style={{ fontSize: "16px", color: BLACK }}>
                RESULTS THAT <span style={{ color: RED }}>MATTER.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────  INDEPENDENCE WEEK EVENT COUPON  ─────────
   Premium navy + red coupon — American luxury aesthetic. Reused on desktop
   (floating top-right of meds image) AND mobile (inline after the wait-a-week
   stamp). Tap → opens assessment modal. Brand: navy(#0A2342) bg, red(#C8102E)
   border + accents, white type. Subtle hover lift. */
function SummerSpecialCoupon({ onCta, compact = false }) {
  const NAVY = "#0A2342";
  const NAVY_DARK = "#081B33";
  const FLAG_RED = "#C8102E";
  return (
    <button
      type="button"
      onClick={onCta}
      data-testid="summer-coupon"
      aria-label="Independence Week Event — start as low as $39"
      className={
        "group block w-full text-left overflow-hidden transition-all " +
        (compact ? "" : "hover:-translate-y-0.5 hover:shadow-2xl")
      }
      style={{
        background: NAVY,
        border: `2px solid ${FLAG_RED}`,
        boxShadow: "0 18px 38px rgba(10,35,66,0.45), 0 0 0 1px rgba(200,16,46,0.20)",
      }}
    >
      {/* Top header strip */}
      <div className="flex items-center justify-center gap-1.5 px-3 py-2 text-white font-heavy tracking-[0.16em] border-b"
        style={{ background: NAVY_DARK, borderColor: "rgba(255,255,255,0.08)", fontSize: compact ? "10.5px" : "11.5px" }}>
          <span aria-hidden style={{ fontSize: compact ? "13px" : "14px", lineHeight: 1 }}>🇺🇸</span>
          INDEPENDENCE WEEK EVENT
      </div>
      {/* Body */}
      <div className={"text-center text-white " + (compact ? "px-4 py-3.5" : "px-4 py-4.5")}>
        <p className="font-extrabold tracking-[0.16em] text-white/75"
          style={{ fontSize: compact ? "9.5px" : "10px" }}>
          START AS LOW AS
        </p>
        <div className="font-heavy leading-none my-1.5 flex items-baseline justify-center gap-1"
          style={{ fontSize: compact ? "46px" : "60px" }}>
          <span style={{ color: "#FFFFFF" }}>$39</span>
          <span className="font-extrabold tracking-[0.14em]" style={{ color: FLAG_RED, fontSize: compact ? "10px" : "11px" }}>
            WK 1*
          </span>
        </div>
        <p className="text-[10.5px] text-white/75 leading-snug px-1 mt-2 max-w-[210px] mx-auto">
          Provider-guided weight loss with licensed review &amp; pharmacy fulfillment.
        </p>
        <span className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 font-extrabold tracking-[0.14em] text-white"
          style={{ background: FLAG_RED, fontSize: compact ? "10.5px" : "11.5px" }}>
          CLAIM JULY OFFER →
        </span>
      </div>
    </button>
  );
}

/* ─────────  3-COLUMN BLACK BAND (price · reviews · price match)  ───────── */
function BlackProofBand() {
  return (
    <section className="bg-black text-white" data-testid="proof-band">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-14 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/15 gap-0">
        {/* PRICE */}
        <div className="text-center px-4 py-6 md:py-0" data-testid="proof-price">
          <p className="font-extrabold text-[12px] md:text-[14px] tracking-[0.14em] mb-2">NEW CUSTOMERS STARTING AS LOW AS</p>
          <div className="font-heavy leading-none" style={{ color: RED, fontSize: "clamp(72px, 8vw, 130px)" }}>$39</div>
          <p className="font-extrabold mt-1 tracking-[0.18em] text-[13px] md:text-[14px]">WEEK 1<span style={{ color: RED }}>*</span></p>
        </div>
        {/* REVIEWS */}
        <div className="text-center px-4 py-6 md:py-0 flex flex-col items-center justify-center" data-testid="proof-reviews">
          <div className="flex items-center gap-1 mb-2">
            {[0,1,2,3,4].map((i) => (
              <Star key={i} size={28} fill="#F5B400" strokeWidth={0} />
            ))}
          </div>
          <p className="font-heavy leading-tight" style={{ fontSize: "clamp(18px, 2vw, 26px)" }}>4.9 OUT OF 5 STARS</p>
          <p className="font-bold text-[11px] md:text-[13px] tracking-[0.1em] mt-1 text-white/80">BASED ON 2,000+ REVIEWS</p>
          <div className="mt-3 inline-flex items-center gap-1.5">
            <span className="font-heavy text-[13px]" style={{ color: "#00B67A" }}>★ Trustpilot</span>
            <span className="font-extrabold text-[12px] text-white/85">EXCELLENT</span>
          </div>
        </div>
        {/* PRICE MATCH */}
        <div className="text-center px-4 py-6 md:py-0 flex flex-col justify-center" data-testid="proof-match">
          <p className="font-heavy leading-tight" style={{ fontSize: "clamp(18px, 2.2vw, 28px)" }}>FOUND A LOWER PRICE?</p>
          <p className="font-heavy leading-tight mt-1" style={{ fontSize: "clamp(18px, 2.2vw, 28px)" }}>
            BRING IT TO US.
            <br />
            <span style={{ color: RED }}>WE'LL MATCH IT.*</span>
          </p>
          <p className="text-[12px] md:text-[13px] text-white/75 mt-3 leading-snug">
            Compare apples to apples. Get the best deal.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────  6 TRUST CIRCLES ROW  ───────── */
function TrustCirclesRow() {
  return (
    <section className="bg-white py-10 md:py-14 border-b border-black/10" data-testid="trust-circles">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-3 md:grid-cols-6 gap-5 md:gap-6">
        {TRUST_CIRCLES.map(({ Icon, t, s }) => (
          <div key={t} className="flex flex-col items-center text-center" data-testid={`trust-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div
              className="w-[88px] h-[88px] md:w-[110px] md:h-[110px] rounded-full flex items-center justify-center mb-3"
              style={{ border: `3px solid ${RED}`, boxShadow: "0 10px 24px rgba(225,29,42,0.20)" }}
            >
              <Icon size={36} strokeWidth={2} color={RED} />
            </div>
            <p className="font-heavy text-[12px] md:text-[13px] text-black tracking-tight">{t}</p>
            <p className="font-extrabold text-[10px] md:text-[11px] text-black/70 tracking-tight mt-0.5">{s}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────  REAL MEDICATION CARDS  ───────── */
function RealMedicationSection() {
  const cards = [
    { Icon: FlaskConical, t: "FDA REGISTERED FACILITIES", s: "Safe. Legal. High Quality. You Can Trust." },
    { Icon: Truck,        t: "DELIVERED TO YOUR DOOR",    s: "Fast, discreet shipping on every order." },
    { Icon: Stethoscope,  t: "PROVIDER PRESCRIBED",       s: "Every order reviewed by a licensed provider." },
  ];
  return (
    <section className="bg-[#FAFAF8] py-12 md:py-16" data-testid="real-medication">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10">
        <h2 className="font-heavy text-center mb-8 md:mb-10" style={{ fontSize: "clamp(24px, 3.2vw, 44px)" }}>
          REAL <span style={{ color: RED }}>MEDICATION.</span> REAL <span style={{ color: RED }}>RESULTS.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map(({ Icon, t, s }) => (
            <div
              key={t}
              className="bg-white border rounded-md p-6 flex items-start gap-4"
              style={{ borderColor: "rgba(0,0,0,0.1)", boxShadow: "0 8px 20px rgba(0,0,0,0.04)" }}
              data-testid={`real-card-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="shrink-0 w-12 h-12 flex items-center justify-center rounded" style={{ background: `${RED}14` }}>
                <Icon size={26} strokeWidth={2.2} color={RED} />
              </div>
              <div>
                <p className="font-heavy text-[15px] md:text-[16px] text-black tracking-tight leading-tight mb-1.5">{t}</p>
                <p className="text-[13.5px] text-black/65 leading-snug">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────  3-BAND BOTTOM (NO INSURANCE · BEST DEAL · CALL FORM)  ───────── */
function ThreeBandBottom({ onCta }) {
  const [form, setForm] = useState({ first_name: "", phone: "", email: "" });
  const { open } = useLeadModal();

  const submit = (e) => {
    e.preventDefault();
    open({
      source: "hero_callback_form",
      first_name: form.first_name,
      phone: form.phone,
      email: form.email,
    });
  };

  return (
    <section className="grid grid-cols-1 md:grid-cols-3" data-testid="bottom-3-band">
      {/* RED — no insurance */}
      <div className="text-white px-7 py-12 md:py-14 flex flex-col justify-center" style={{ background: RED }}>
        <h3 className="font-heavy leading-[0.95]" style={{ fontSize: "clamp(28px, 3.4vw, 44px)" }}>
          NO INSURANCE?
          <br />
          NO PROBLEM.
        </h3>
        <p className="mt-5 font-extrabold tracking-[0.06em] text-[12px] md:text-[13px] uppercase leading-snug">
          Most people think they need insurance. They don't.
        </p>
      </div>
      {/* BLACK — best deal */}
      <div className="bg-black text-white px-7 py-12 md:py-14 flex flex-col justify-center">
        <h3 className="font-heavy leading-[0.95]" style={{ fontSize: "clamp(28px, 3.4vw, 44px)" }}>
          THE BEST DEAL.
          <br />
          <span style={{ color: RED }}>PERIOD.</span>
        </h3>
        <p className="mt-5 font-extrabold tracking-[0.06em] text-[12px] md:text-[13px] uppercase leading-snug">
          Why pay more for the same medication?
        </p>
      </div>
      {/* RED — call form */}
      <div className="text-white px-7 py-10 md:py-12" style={{ background: RED }}>
        <p className="font-heavy text-center leading-tight" style={{ fontSize: "clamp(15px, 1.5vw, 18px)" }}>
          WE WILL CALL YOU
          <br />
          IN THE NEXT 30 MINUTES!<sup>*</sup>
        </p>
        <p className="text-center text-[10.5px] mt-1.5 font-bold tracking-[0.14em] text-white/85">DURING BUSINESS HOURS</p>
        <form onSubmit={submit} className="mt-4 space-y-2.5" data-testid="callback-form">
          <input
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            type="text"
            required
            placeholder="FIRST NAME"
            className="w-full px-3 py-2.5 bg-white text-black font-extrabold text-[12px] tracking-[0.06em] outline-none"
            data-testid="callback-first-name"
          />
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            type="tel"
            required
            placeholder="PHONE NUMBER"
            className="w-full px-3 py-2.5 bg-white text-black font-extrabold text-[12px] tracking-[0.06em] outline-none"
            data-testid="callback-phone"
          />
          <input
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            type="email"
            required
            placeholder="EMAIL ADDRESS"
            className="w-full px-3 py-2.5 bg-white text-black font-extrabold text-[12px] tracking-[0.06em] outline-none"
            data-testid="callback-email"
          />
          <button
            type="submit"
            data-testid="callback-submit"
            className="w-full bg-black text-white font-extrabold text-[12px] tracking-[0.12em] py-3.5 hover:bg-black/90 transition-colors"
          >
            HAVE A BESTRX SPECIALIST CALL ME
          </button>
        </form>
      </div>
    </section>
  );
}

/* ─────────  FOOTER TAGLINE STRIP  ───────── */
function FooterTagline() {
  return (
    <div className="bg-white py-6 md:py-8 border-t border-black/10" data-testid="footer-tagline">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
        <p className="font-heavy text-[14px] md:text-[18px] tracking-tight text-black">BETTER QUALITY.</p>
        <p className="font-heavy text-[14px] md:text-[18px] tracking-tight text-black">BETTER PRICES.</p>
        <p className="font-heavy text-[14px] md:text-[18px] tracking-tight text-black">BETTER RESULTS.</p>
        <p className="font-heavy text-[14px] md:text-[18px] tracking-tight text-black">
          BEST<span style={{ color: RED }}>RX</span> IS YOUR NEW CHOICE.
        </p>
      </div>
    </div>
  );
}

/* ─────────  MAIN  ───────── */
export default function HeroV2() {
  const { open } = useLeadModal();
  const onCta = () => open({ source: "hero_v2" });

  // Single-screen hero — TopHeader + HeroCore only. The path picker (in
  // MedicationZone) lives right below, so visitors reach medication selection
  // within one scroll. The decommissioned sections (BlackProofBand,
  // TrustCirclesRow, RealMedicationSection, ThreeBandBottom, FooterTagline)
  // remain in this file as named exports so dedicated pages (e.g. /about,
  // /pricing) can compose them later — but they no longer render on /.
  return (
    <div className="bg-white" data-testid="hero-v2">
      <TopHeader onCta={onCta} />
      <HeroCore onCta={onCta} />
    </div>
  );
}

// Named re-exports so dedicated pages can compose these previously-stacked
// sections without us having to maintain duplicated markup.
export { BlackProofBand, TrustCirclesRow, RealMedicationSection, ThreeBandBottom, FooterTagline };
